import { callEdgeFunction } from './supabase';
import type {
  AffiliateApplication,
  AffiliatePartner,
  AffiliatePartnerDetail,
  AffiliateDashboardStats,
} from '../types/affiliate';

const ADMIN_FN = 'admin-manage-affiliates';
const PORTAL_FN = 'affiliate-portal';

// ── Admin Management ──

export async function listApplications(
  page = 1,
  pageSize = 20,
  status?: string,
): Promise<{ applications: AffiliateApplication[]; total: number }> {
  return callEdgeFunction(ADMIN_FN, {
    action: 'list_applications',
    page,
    page_size: pageSize,
    ...(status && status !== 'all' ? { status } : {}),
  });
}

export async function reviewApplication(
  applicationId: string,
  decision: 'approved' | 'rejected',
  rejectionReason?: string,
): Promise<{ success: boolean; decision: string; partner_id?: string; promo_code?: string }> {
  return callEdgeFunction(ADMIN_FN, {
    action: 'review_application',
    application_id: applicationId,
    decision,
    ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
  });
}

export async function listPartners(
  page = 1,
  pageSize = 20,
): Promise<{ partners: AffiliatePartner[]; total: number }> {
  return callEdgeFunction(ADMIN_FN, {
    action: 'list_partners',
    page,
    page_size: pageSize,
  });
}

export async function getPartnerDetail(
  partnerId: string,
): Promise<AffiliatePartnerDetail> {
  return callEdgeFunction(ADMIN_FN, {
    action: 'get_partner_detail',
    partner_id: partnerId,
  });
}

export async function togglePartner(
  partnerId: string,
  reason?: string,
): Promise<{ success: boolean; is_active: boolean; message: string }> {
  return callEdgeFunction(ADMIN_FN, {
    action: 'toggle_partner',
    partner_id: partnerId,
    ...(reason ? { reason } : {}),
  });
}

export async function getDashboardStats(): Promise<AffiliateDashboardStats> {
  return callEdgeFunction(ADMIN_FN, {
    action: 'get_dashboard_stats',
  });
}

// ── Affiliate Portal (for affiliate users) ──

export async function getPortalDashboard() {
  return callEdgeFunction(PORTAL_FN, { action: 'get_dashboard' });
}

export async function getPortalReferrals(page = 1, pageSize = 20) {
  return callEdgeFunction(PORTAL_FN, { action: 'get_referrals', page, page_size: pageSize });
}

export async function getPortalCommissions(page = 1, pageSize = 20) {
  return callEdgeFunction(PORTAL_FN, { action: 'get_commissions', page, page_size: pageSize });
}

export async function getPortalPayouts(page = 1, pageSize = 20) {
  return callEdgeFunction(PORTAL_FN, { action: 'get_payouts', page, page_size: pageSize });
}

export async function getPortalProfile() {
  return callEdgeFunction(PORTAL_FN, { action: 'get_profile' });
}

export async function updatePortalProfile(updates: Record<string, string>) {
  return callEdgeFunction(PORTAL_FN, { action: 'update_profile', ...updates });
}

export async function setupStripeConnect(options?: { country?: string; return_url?: string; refresh_url?: string }) {
  return callEdgeFunction(PORTAL_FN, { action: 'setup_stripe_connect', ...options });
}

export async function checkStripeConnect() {
  return callEdgeFunction(PORTAL_FN, { action: 'check_stripe_connect' });
}
